import { z } from "zod";
import { requireValidityOrder } from "@portal/common/validitySchema";

// Mensaje literal de SPEC-211 §9 (`DETAIL_REQUIRED`): es el contrato de UX,
// no una redacción libre — el servidor devuelve el mismo código si el
// cliente lo saltea (mismo criterio que `COST_INVALID_MESSAGE` en
// rewards/rewardSchemas.ts).
export const DETAIL_REQUIRED_MESSAGE =
  "El detalle es obligatorio: es lo que el miembro lee";

// SPEC-211 §4.1, criterio 4: con `requiresScheduling` activo, el módulo
// exige tipo de servicio y al menos un taller asociado — sin ellos el
// servidor rechaza con `FIELDS_REQUIRED` (SPEC-211 §9 no fija un texto
// literal para este código, a diferencia de los otros tres).
export const FIELDS_REQUIRED_MESSAGE =
  "Requiere agendamiento necesita un tipo de servicio y al menos un taller asociado";

// T2 (2026-09-19, qa-bug-matrix-2026-09.md §4.1, #15/#36/#37/#38): un ítem
// vacío de "Qué incluye" pasaba esta validación (solo `.trim()`, sin
// `.min(1)`) y viajaba al servidor, que lo rechazaba con `INVALID_REQUEST` —
// un código que `BenefitsScreen.tsx` no mapeaba, así que el operador
// guardaba y no pasaba nada. El error se detecta ahora donde está el
// problema.
export const INCLUDE_TEXT_REQUIRED_MESSAGE =
  'Cada ítem de "Qué incluye" necesita texto en español';

const includeSchema = z.object({
  textEs: z.string().trim().min(1, INCLUDE_TEXT_REQUIRED_MESSAGE),
  textEn: z.string(),
});

export const benefitFormSchema = z
  .object({
    nameEs: z.string().trim().min(1, "El nombre del beneficio es obligatorio"),
    nameEn: z.string(),
    detailEs: z.string().trim().min(1, DETAIL_REQUIRED_MESSAGE),
    detailEn: z.string(),
    icon: z.string(),
    // SPEC-211, hallazgo de pérdida silenciosa: el editor no tiene campo de
    // categoría todavía (agregar uno es alcance nuevo, ver reporte), pero el
    // id que ya tiene el beneficio viaja por acá para que una edición que no
    // lo toca no lo borre — `benefit.service.ts` resuelve `categoryId ??
    // null` y hoy `buildInput` nunca lo emitía.
    categoryId: z.string().optional(),
    redemptionType: z.enum(["once", "monthly", "yearly"]),
    requiresScheduling: z.boolean(),
    serviceTypeId: z.string(),
    includes: z.array(includeSchema),
    merchantIds: z.array(z.string()),
    workshopIds: z.array(z.string()),
    validFrom: z.string(),
    validUntil: z.string(),
    active: z.boolean(),
  })
  .superRefine((values, ctx) => {
    requireValidityOrder(values, ctx);
    if (!values.requiresScheduling) return;
    if (!values.serviceTypeId.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["serviceTypeId"],
        message: FIELDS_REQUIRED_MESSAGE,
      });
    }
    if (values.workshopIds.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["workshopIds"],
        message: FIELDS_REQUIRED_MESSAGE,
      });
    }
  });
export type BenefitFormValues = z.infer<typeof benefitFormSchema>;
